import { useMotionValueEvent, useScroll } from 'framer-motion';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { usePathname } from '@/core/i18n/routing';
import getBffCartItemCount from '@/core/utils/get-bff-cart-item-count';
import { useHeaderSummaryConfig } from '@/ui/context/header-summary-config';
import useIsNonEvaCountry from '@/ui/hooks/use-is-non-eva-country';
import useOrder from './use-order';

const SHOW_THRESHOLD_OFFSET = 20;
const HIDE_THRESHOLD_OFFSET = 20;
const VIEWPORT_HEIGHT_RATIO = 0.95;

function getViewportHeight(): number {
  if (typeof window === 'undefined') {
    return 0;
  }

  return window.innerHeight * VIEWPORT_HEIGHT_RATIO;
}

const useHeaderSummaryVisibility = () => {
  const isNonEvaCountry = useIsNonEvaCountry();
  const pathname = usePathname();
  const [isSummaryVisible, setIsSummaryVisible] = useState(false);
  const headerSummaryConfig = useHeaderSummaryConfig();
  const summaryItems = headerSummaryConfig?.items;
  const { scrollY } = useScroll();
  const { cart } = useOrder();
  const totalItems = getBffCartItemCount(cart);
  const isSummaryVisibleRef = useRef(false);

  const shouldShowSummary =
    !isNonEvaCountry &&
    (summaryItems?.length ?? 0) > 0 &&
    totalItems === 0;

  const shouldShowSummaryRef = useRef(shouldShowSummary);
  shouldShowSummaryRef.current = shouldShowSummary;

  useEffect(() => {
    if (!shouldShowSummary) {
      isSummaryVisibleRef.current = false;
      setIsSummaryVisible(false);
    }
  }, [shouldShowSummary]);

  useEffect(() => {
    isSummaryVisibleRef.current = isSummaryVisible;
  }, [isSummaryVisible]);

  useEffect(() => {
    isSummaryVisibleRef.current = false;
    setIsSummaryVisible(false);
  }, [pathname]);

  const evaluateVisibility = useCallback((scrollPosition: number) => {
    if (!shouldShowSummaryRef.current) {
      if (isSummaryVisibleRef.current) {
        isSummaryVisibleRef.current = false;
        setIsSummaryVisible(false);
      }
      return;
    }

    const viewportHeight = getViewportHeight();
    const showThreshold = viewportHeight + SHOW_THRESHOLD_OFFSET;
    const hideThreshold = viewportHeight - HIDE_THRESHOLD_OFFSET;
    const currentlyVisible = isSummaryVisibleRef.current;
    const shouldShow = currentlyVisible
      ? scrollPosition > hideThreshold
      : scrollPosition > showThreshold;

    if (shouldShow !== currentlyVisible) {
      isSummaryVisibleRef.current = shouldShow;
      setIsSummaryVisible(shouldShow);
    }
  }, []);

  useMotionValueEvent(scrollY, 'change', latest => {
    evaluateVisibility(latest);
  });

  useLayoutEffect(() => {
    evaluateVisibility(scrollY.get());
  }, [evaluateVisibility, scrollY, summaryItems, shouldShowSummary, pathname]);

  if (isNonEvaCountry) {
    return false;
  }

  return isSummaryVisible;
};

export default useHeaderSummaryVisibility;
